import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FORMAT_HOUR_FULL, FORMAT_MONTH_SHORT } from '../../../config';
import AppointmentService from '../../../services/appointmentService';
import { Appointment } from '../../../services/dto/Common';
import { User } from '../../../services/dto/Security';
import {
  checkIsCanMeeting,
  getErrorString,
  getName,
} from '../../../services/utils';
import { connectSocket } from '../../../socket.io';
import { loadAppointments } from '../../../store/actions/appointments';
import { IAppState } from '../../../store/reducers';
import { AppointmentsState } from '../../../store/reducers/appointments';
import { ProfileIcon } from '../../ProfileIcon';
import GetReady from '../GetReady';
import ArrowButton from './ArrowButton';
import CancelModal from './CancelModal';
import './styles.scss';
import { Socket } from 'socket.io-client';
import { Box, Button, Typography, Tooltip, Card, CardContent, CardHeader, IconButton, Paper } from '@mui/material';
import { InfoOutlined, Today } from '@mui/icons-material';
import ProviderCard from '../../../containers/WaitingRoomPage/WaitingRoomProviderCard';

export interface INextAppointmentProps {
  apts?: Appointment[];
  patientId?: string;
  onOpenProviderInfo: (p: User) => void;
}

export const NextAppointment = (props: INextAppointmentProps) => {
  const history = useHistory();
  const { appointments } = useSelector<IAppState, AppointmentsState>(
    (s) => s.appointments,
  );
  const [nearestApt, setNearestApt] = useState<Appointment>();
  const [appointmentList, setAppointmentList] = useState<any>([]);
  const [appointmentIndex, setAppointmentIndex] = useState(0);
  const [cancelModal, setCancelModal] = useState(false);
  const nearestAptRef = useRef<Appointment>();
  const aptRefreshTimer = useRef<any>();
  const dispatch = useDispatch();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    const queryAppointmentInterval = () => {
      interval = setInterval(() => {
        dispatch(
          loadAppointments({
            types: ["upcoming", "ongoing"],
            patientId: props.patientId,
          }),
        );
      }, 36000);
    };

    if (!aptRefreshTimer.current && nearestApt) {
      const startTimeMS = moment
        .utc(nearestApt?.startTime)
        .diff(moment(), "milliseconds");

      aptRefreshTimer.current = setTimeout(() => {
        queryAppointmentInterval();
      }, startTimeMS);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(aptRefreshTimer.current);
    };
  }, [nearestApt]);

  useEffect(() => {
    let socket: Socket;
    if (nearestApt) {
      socket = connectSocket();
      socket.on("connect", () => {
        // Will assign user to socket room via appointmentId
        socket.emit("join-meeting", {
          participantType: "Patient",
          appointmentId: nearestApt?.id,
        });
      });
    }

    return () => {
      socket?.close();
    };
  }, [nearestApt]);

  useEffect(() => {
    const data = appointments.data || [];
    const sortedAppointments = data.sort(
      (a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf(),
    );
    setAppointmentList(data);
    injectApt(
      sortedAppointments.find((apt) =>
        apt.description.includes("Consultation"),
      ),
    );
  }, [appointments]);

  const getNearestAppointment = (aptList: any) => {
    let nearestIndex = 0;
    aptList.forEach((time: string, i: number) => {
      if (moment(time).isBefore(time[i == 0 ? i : i - 1])) {
        nearestIndex = i;
      }
    });

    return aptList[nearestIndex];
  };
  const injectApt = (apt?: Appointment) => {
    nearestAptRef.current = apt;
    apt && setNearestApt(getNearestAppointment([apt]));
  };

  const handleDeleteAppointment = async (form: {
    reason: string;
    cancelDescription: string;
  }) => {
    try {
      await AppointmentService.deleteAppointment(nearestApt?.id || "", {
        reason: form.reason,
        cancelDescription: form.cancelDescription,
      });
      dispatch(
        loadAppointments({
          types: ["upcoming", "ongoing"],
          patientId: props.patientId,
        }),
      );
      setNearestApt(undefined);
      nearestAptRef.current = undefined;
      setCancelModal(false);
    } catch (err) {
      toast.error(
        (err as any)?.message ||
          "Failed to cancel appointment. Try again, if this continues please contact us.",
      );
    }
  };

  const isMoreThan24Hours = (apt: Appointment) =>
    moment(apt.startTime).diff(moment(), "hours") >= 24;

  if (nearestApt && checkIsCanMeeting(nearestApt)) {
    return <GetReady apt={nearestApt} />;
  }

  const firstApt = appointmentList[appointmentIndex];
  return (
    <Paper elevation={0} sx={{ height: "548px" }}>
      <Box
        sx={{
          maxWidth: "90%",
          margin: "0 auto",
          paddingTop: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {/* Header */}
        <Typography variant="h5" fontWeight="bold">
          Next Appointment
        </Typography>

        {/* Loading */}
        {appointments.loading && (
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        )}

        {/* Error */}
        {appointments.err && (
          <Typography variant="body1" color="text.secondary">
            {getErrorString(appointments.err)}
          </Typography>
        )}

        {/* No Appointments */}
        {!appointmentList.length && (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Today sx={{ fontSize: "64px" }} />
            <Typography variant="body1" color="text.secondary">
              No Appointments Scheduled
            </Typography>
            <Button
              variant="contained"
              onClick={() =>
                history.push(
                  `/scheduleAppointmentPage?aptCount=${
                    (appointments.data?.length || 0) + 1
                  }`,
                )
              }
            >
              Schedule Appointment
            </Button>
          </Box>
        )}
        {appointmentList.length > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <ArrowButton
              disabled={appointmentIndex === 0}
              prev={() => setAppointmentIndex(appointmentIndex - 1)}
            />
            <ArrowButton
              disabled={appointmentList.length - 1 === appointmentIndex}
              next={() => setAppointmentIndex(appointmentIndex + 1)}
            />
          </div>
        )}

        {/* Appointment Card */}
        {firstApt && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant="h6">{firstApt.description}</Typography>

            {/* Provider Card */}
            <ProviderCard provider={firstApt.provider} />

            {/* Date Card */}
            <Card elevation={0} sx={{ backgroundColor: "background.default" }}>
              <CardHeader
                avatar={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      border: "1px solid black",
                      borderRadius: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        p: "0 16px",
                        backgroundColor: "red",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        borderRadius: "12px 12px 0 0",
                      }}
                    >
                      <Typography variant="body1" color="common.white">
                        {moment(firstApt.startTime).format(FORMAT_MONTH_SHORT)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: "2px 16px",
                        backgroundColor: "white",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        borderRadius: "0 0 12px 12px",
                      }}
                    >
                      <Typography variant="h6" color="common.black">
                        {moment(firstApt.startTime).format("DD")}
                      </Typography>
                    </Box>
                  </Box>
                }
                title={
                  <Typography variant="h6">
                    {`${moment(firstApt.startTime).format("dddd")}, 
                      ${moment(firstApt.startTime).format(FORMAT_MONTH_SHORT)} 
                      ${moment(firstApt.startTime).format("Do")}`}
                  </Typography>
                }
                subheader={
                  <Typography variant="body2">{`${moment(
                    firstApt.startTime,
                  ).format(
                    FORMAT_HOUR_FULL,
                  )}-${moment(firstApt.endTime).format(FORMAT_HOUR_FULL)}`}</Typography>
                }
              />
            </Card>

            {/* Buttons */}
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() =>
                  history.push(
                    `/editScheduleAppointmentPage/${firstApt.id}?aptCount=${
                      appointments.data?.length || 0
                    }&provider=${firstApt.providerId}`,
                  )
                }
              >
                Edit
              </Button>
              <Tooltip
                title={
                  isMoreThan24Hours(firstApt)
                    ? ""
                    : "Cannot cancel appointment less than 24 hours in advance"
                }
              >
                {/* <span> needed to allow tooltip even when disabled */}
                <span>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setCancelModal(true)}
                    disabled={!isMoreThan24Hours(firstApt)}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {cancelModal && (
              <CancelModal
                onDelete={handleDeleteAppointment}
                onClose={() => setCancelModal(false)}
              />
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default NextAppointment;
